import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Users } from 'lucide-react';
import { useMyTeams } from '../hooks/useMyTeams.js';
import { useOrgStore } from '../store/org-store.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSection,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './tailgrids/core/dropdown.js';

/**
 * Selector for the active team context, mounted in the app header.
 *
 * - Selecting a team sets `activeTeamId` in the org store, which injects `X-Team-Id` into every
 *   subsequent API request.
 * - "All teams" clears the selection: the person sees every team they belong to plus org-wide entities.
 * - Hidden while the person has fewer than 2 teams; a single-team person needs no selector.
 */
export function TeamSelector() {
  const { data: teams = [] } = useMyTeams();
  const activeTeamId = useOrgStore((s) => s.activeTeamId);
  const setActiveTeam = useOrgStore((s) => s.setActiveTeam);
  const queryClient = useQueryClient();

  // Switching teams changes what the server returns — refetch every cached query except the teams
  // list itself under the new X-Team-Id header.
  const switchTeam = (teamId: number | null) => {
    if (teamId === activeTeamId) return;
    setActiveTeam(teamId);
    queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string | undefined) !== 'my-teams' });
  };

  if (teams.length < 2) return null;
  const active = teams.find((t) => t.id === activeTeamId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Team context"
        className="flex h-10 items-center gap-2 rounded-lg border border-card-border bg-card-background px-3 text-sm text-text-secondary shadow-xs transition-colors hover:bg-background-gray-primary focus-visible:ring-4 focus-visible:ring-input-primary-focus-border/20"
      >
        <Users size={16} className="text-icon-tertiary" />
        <span className="font-medium text-text-primary">{active?.name ?? 'All teams'}</span>
        <ChevronDown size={14} className="text-icon-tertiary" />
      </DropdownMenuTrigger>
      <DropdownMenuContent placement="bottom end" className="w-60 p-1.5">
        <DropdownMenuSection>
          <DropdownMenuItem
            id="all"
            textValue="All teams"
            onAction={() => switchTeam(null)}
            className="justify-between px-3 py-2"
          >
            <span>All teams</span>
            {activeTeamId == null && <Check size={16} className="text-primary-500" />}
          </DropdownMenuItem>
        </DropdownMenuSection>
        <DropdownMenuSeparator />
        <DropdownMenuSection>
          {teams.map((team) => (
            <DropdownMenuItem
              key={team.id}
              id={`team-${team.id}`}
              textValue={team.name}
              onAction={() => switchTeam(team.id)}
              className="justify-between px-3 py-2"
            >
              <span>
                {team.name}
                {team.role === 'lead' && <span className="ml-1.5 text-xs text-text-tertiary">(lead)</span>}
              </span>
              {activeTeamId === team.id && <Check size={16} className="text-primary-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSection>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
